import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { ApiResponse, PageableDto } from '@shared-models';
import { API_CONFIG, ApiConfig } from '../../api-config';
import { mapMutationMessage } from '../../../utils/map-pageable';

/** Shape API de un archivo digital de empresa. */
export interface CompanyDigitalFileDto {
  id: string;
  companyId?: string;
  name: string;
  validityDate?: string | null;
  noExpiration?: boolean;
  fileUrl?: string;
  originalName?: string;
  contentType?: string;
  fileSize?: number;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type CompanyDigitalFileWritePayload = {
  name: string;
  noExpiration: boolean;
  validityDate?: string | null;
};

export type CompanyDigitalFileListParams = {
  page?: number;
  size?: number;
  sort?: string;
  direction?: 'asc' | 'desc';
  status?: string;
  search?: string;
};

/**
 * Archivo digital de empresa.
 *   GET    /api/structure/companies/{companyId}/digital-files
 *   POST   /api/structure/companies/{companyId}/digital-files  (multipart)
 *   PUT    /api/structure/companies/{companyId}/digital-files/{id}
 *   PATCH  /api/structure/companies/{companyId}/digital-files/{id}/status
 *   DELETE /api/structure/companies/{companyId}/digital-files/{id}
 */
@Injectable({ providedIn: 'root' })
export class CompanyDigitalFileService {
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private resource(companyId: string): string {
    return `${this.apiConfig.apiUrl}/structure/companies/${companyId}/digital-files`;
  }

  list(
    companyId: string,
    params: CompanyDigitalFileListParams = {},
  ): Observable<CompanyDigitalFileDto[]> {
    let httpParams = new HttpParams()
      .set('page', String(params.page ?? 0))
      .set('size', String(params.size ?? 100))
      .set('sort', params.sort ?? 'createdAt')
      .set('direction', params.direction ?? 'desc');

    if (params.status?.trim()) {
      httpParams = httpParams.set('status', params.status.trim());
    }
    if (params.search?.trim()) {
      httpParams = httpParams.set('search', params.search.trim());
    }

    return this.http
      .get<
        ApiResponse<
          CompanyDigitalFileDto[] | PageableDto<CompanyDigitalFileDto>
        >
      >(this.resource(companyId), { params: httpParams })
      .pipe(map((res) => this.unwrapList(res.data)));
  }

  create(
    companyId: string,
    payload: CompanyDigitalFileWritePayload,
    file: File,
  ): Observable<CompanyDigitalFileDto> {
    return this.http
      .post<ApiResponse<CompanyDigitalFileDto>>(
        this.resource(companyId),
        this.toFormData(payload, file),
      )
      .pipe(map((res) => res.data as CompanyDigitalFileDto));
  }

  update(
    companyId: string,
    fileId: string,
    payload: CompanyDigitalFileWritePayload,
    file?: File | null,
  ): Observable<CompanyDigitalFileDto> {
    return this.http
      .put<ApiResponse<CompanyDigitalFileDto>>(
        `${this.resource(companyId)}/${fileId}`,
        this.toFormData(payload, file),
      )
      .pipe(map((res) => res.data as CompanyDigitalFileDto));
  }

  updateStatus(
    companyId: string,
    fileId: string,
    status: 'active' | 'inactive',
  ): Observable<{ message: string }> {
    const apiStatus = status === 'inactive' ? 'INACTIVE' : 'ACTIVE';
    return this.http
      .patch<{ success?: boolean; message?: string }>(
        `${this.resource(companyId)}/${fileId}/status`,
        { status: apiStatus },
      )
      .pipe(
        map((response) =>
          mapMutationMessage(response, 'Estatus actualizado exitosamente'),
        ),
      );
  }

  delete(
    companyId: string,
    fileId: string,
  ): Observable<{ message: string }> {
    return this.http
      .delete<{ success?: boolean; message?: string }>(
        `${this.resource(companyId)}/${fileId}`,
      )
      .pipe(
        map((response) =>
          mapMutationMessage(response, 'Archivo eliminado exitosamente'),
        ),
      );
  }

  private toFormData(
    payload: CompanyDigitalFileWritePayload,
    file?: File | null,
  ): FormData {
    const formData = new FormData();
    formData.append('name', payload.name);
    formData.append('noExpiration', String(payload.noExpiration));
    if (!payload.noExpiration && payload.validityDate) {
      formData.append('validityDate', payload.validityDate);
    }
    if (file) {
      formData.append('file', file);
    }
    return formData;
  }

  private unwrapList(
    data:
      | CompanyDigitalFileDto[]
      | PageableDto<CompanyDigitalFileDto>
      | null
      | undefined,
  ): CompanyDigitalFileDto[] {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.content)) return data.content;
    return [];
  }
}
